/**
 * Obfuscation for values persisted to localStorage, so they are not readable
 * at a glance in the browser's devtools.
 *
 * The seed is read and written through window.localStorage directly instead
 * of utilities/storage on purpose: the storage facade broadcasts every
 * written value in a window event, which would leak the seed to any listener.
 *
 * Known limitation: a plain value that happens to start with the marker is
 * treated as obfuscated and will fail to decode.
 */
const SEED_STORAGEID = 'appInstanceSeed'
const MARKER = 'v1:'

function decodeSeed(seed) {
  try {
    const bytes = Uint8Array.from(atob(seed), (character) =>
      character.charCodeAt(0),
    )

    return bytes.length ? bytes : null
  } catch (error) {
    return null
  }
}

function getSeedBytes() {
  let seedBytes = decodeSeed(localStorage.getItem(SEED_STORAGEID) || '')

  if (!seedBytes) {
    // Missing, empty or corrupted seed: (re)generate one. Values obfuscated
    // with a previous seed become undecodable, which deobfuscate turns into
    // an empty string.
    seedBytes = window.crypto.getRandomValues(new Uint8Array(32))

    try {
      localStorage.setItem(
        SEED_STORAGEID,
        btoa(String.fromCharCode(...seedBytes)),
      )
    } catch (error) {
      // Storage is full or unavailable: keep the in-memory seed so callers
      // still work for the rest of this session.
    }
  }

  return seedBytes
}

function xor(bytes, seedBytes) {
  return bytes.map((byte, index) => byte ^ seedBytes[index % seedBytes.length])
}

/**
 * Returns an unreadable, base64-looking representation of value, prefixed
 * with a marker so deobfuscate can tell it apart from legacy plain values.
 * Empty values pass through unchanged.
 */
export function obfuscate(value) {
  if (!value) {
    return value
  }

  const scrambled = xor(new TextEncoder().encode(value), getSeedBytes())

  return MARKER + btoa(String.fromCharCode(...scrambled))
}

/**
 * Inverse of obfuscate. Values without the marker (legacy plain values) are
 * returned as-is so callers can migrate them. Returns an empty string when
 * a marked value cannot be decoded (e.g. the seed was cleared).
 */
export function deobfuscate(value) {
  if (!value || !value.startsWith(MARKER)) {
    return value
  }

  try {
    const scrambled = Uint8Array.from(
      atob(value.slice(MARKER.length)),
      (character) => character.charCodeAt(0),
    )

    return new TextDecoder('utf-8', { fatal: true }).decode(
      xor(scrambled, getSeedBytes()),
    )
  } catch (error) {
    return ''
  }
}
